import React, { useCallback, useEffect, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';

// SOURCE: components/admin/PaymentsTableToolbar.tsx:43-51 (useDebounced) e :246-258 (bloco
// Autocomplete) — extração do padrão triplicado naquele arquivo (atleta/torneio/categoria) para
// um componente controlado, puramente apresentacional. Não importa nenhum service/gql — quem
// chama passa `fetchOptions` já pronta (ex.: buscarLigasPorNome).

export interface FiltroAutocompleteOption {
  id: string;
  label: string;
}

export interface FiltroAutocompleteProps {
  label: string;
  value: FiltroAutocompleteOption | null;
  onChange: (value: FiltroAutocompleteOption | null) => void;
  fetchOptions: (query: string) => Promise<FiltroAutocompleteOption[]>;
  disabled?: boolean;
  debounceMs?: number;
  // Quantos caracteres o operador precisa digitar antes de a busca sair. Existe porque 1-2
  // caracteres casam com quase tudo: a consulta é cara, volta um resultado inútil e ainda
  // atrapalha quem está no meio da digitação. O debounce sozinho não resolve isso -- ele adia a
  // requisição, mas continua fazendo a de "a".
  minCaracteres?: number;
}

const MIN_CARACTERES_PADRAO = 3;

const useDebounced = (fn: (...args: any[]) => void, delay: number) => {
  const ref = useRef<number | undefined>(undefined);
  const cb = useCallback(
    (...args: any[]) => {
      if (ref.current) window.clearTimeout(ref.current);
      ref.current = window.setTimeout(() => fn(...args), delay);
    },
    [fn, delay],
  );
  useEffect(() => () => { if (ref.current) window.clearTimeout(ref.current); }, []);
  return cb;
};

const FiltroAutocomplete: React.FC<FiltroAutocompleteProps> = ({
  label,
  value,
  onChange,
  fetchOptions,
  disabled,
  debounceMs = 350,
  minCaracteres = MIN_CARACTERES_PADRAO,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<FiltroAutocompleteOption[]>([]);
  const [loading, setLoading] = useState(false);

  const doFetch = useCallback(
    async (query: string) => {
      setLoading(true);
      try {
        const items = await fetchOptions(query);
        setOptions(items);
      } finally {
        setLoading(false);
      }
    },
    [fetchOptions],
  );

  const debouncedFetch = useDebounced(doFetch, debounceMs);

  const termoBuscavel = inputValue.trim().length >= minCaracteres;

  useEffect(() => {
    if (termoBuscavel) {
      debouncedFetch(inputValue);
      return;
    }
    // Abaixo do mínimo, as opções da busca anterior são DESCARTADAS. Mantê-las deixaria na tela
    // uma lista que não corresponde mais ao que está escrito no campo -- pior que lista vazia,
    // porque parece resultado.
    setOptions([]);
  }, [inputValue, termoBuscavel]);

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option) => option.label}
      loading={loading}
      disabled={disabled}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => setInputValue(newInputValue)}
      value={value}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      onChange={(_event, newValue) => onChange(newValue)}
      // Dois estados distintos, nunca a mesma frase: "ainda não busquei" é diferente de "busquei
      // e não achei nada". Com o texto único do MUI ("No options"), digitar 2 letras pareceria
      // dizer que a liga não existe.
      noOptionsText={
        termoBuscavel ? 'Nenhum resultado' : `Digite ao menos ${minCaracteres} caracteres`
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default FiltroAutocomplete;
